import React, { useState } from 'react';
import type { Movimentacao, StatusMovimentacao, MovimentacaoItemIntake, ConfigRegrasCliente, TipoSolicitacao } from '../../../models';
import type { ClientSummary } from '@/features/Clients/Data';
import { mesclarEquipamentosMovimentacao, calcularAlertaMovimentacao, CLASSE_COR_POR_NIVEL_ALERTA } from '../../../models';
import { TIPO_FLUXO_LABELS, TIPO_EQUIPAMENTO_LABELS } from '../solicitacaoDetailsPage.definitions';
import { etapasDaMovimentacao } from '../etapasMovimentacao';
import { resumoLogistica, resumoTermo } from '../movimentacaoResumo';
import EquipamentosMovimentacaoLista from './EquipamentosMovimentacaoLista';
import LinhaSecaoMovimentacao from './LinhaSecaoMovimentacao';
import TermoPassos from './TermoPassos';
import GavetaMovimentacao from './GavetaMovimentacao';
import AlocacaoMovimentacaoLinha from './AlocacaoMovimentacaoLinha';
import { useMovimentacaoMutations } from '../../../hooks/data';
import MovimentacaoEquipamentoItem from './MovimentacaoEquipamentoItem';
import MovimentacaoLogisticaSection from './MovimentacaoLogisticaSection';
import FotosMovimentacaoSection from './FotosMovimentacaoSection';
import ExcluirMovimentacaoButton from './ExcluirMovimentacaoButton';
import DataPrevistaMovimentacao from './DataPrevistaMovimentacao';
import StatusMovimentacaoCampo from './StatusMovimentacaoCampo';

interface MovimentacaoCardProps {
  solicitacaoId: string;
  movimentacao: Movimentacao;
  podeEditar: boolean;
  /** SI ou quem abriu a Solicitação (inclui o time do DP) — só a data prevista. */
  podeEditarDataPrevista: boolean;
  /** Lixeira (exclusão com motivo, ver Movimentacao.excluida) — só SI; o card ainda
   * esconde quando a Movimentação já foi entregue. */
  podeExcluir: boolean;
  /** Ver SolicitacaoDetailsContent — SI ou o solicitante, independente de qual aba abriu. */
  podeVerSenha: boolean;
  /** Colaborador dono da Solicitação — pode registrar data de postagem (devolução) ou
   * recebimento (envio), mesmo sem `podeEditar` (ver MovimentacaoLogisticaSection). */
  isColaboradorAlvo: boolean;
  /** Ver SolicitacaoDetailsContent — só alguns Clientes permitem equipamento próprio
   * (ConfigRegrasCliente.permiteEquipamentoCliente, configurável em ConfiguracoesDrawer). */
  propriedadeVisivel: boolean;
  /** Ver SolicitacaoDetailsContent — só alguns Clientes usam máquina homologada
   * (ConfigRegrasCliente.usaEquipamentoHomologado, configurável em ConfiguracoesDrawer). */
  homologadoVisivel: boolean;
  /** Usado só pelo GerarTermoButton (nome/CPF do colaborador, texto de Observações da
   * Solicitação). */
  colaboradorId: string | null;
  /** Usado pelos botões "Chamar no WhatsApp/Teams" (ver MovimentacaoLogisticaSection) —
   * Admissão/Desligamento só mostram WhatsApp. */
  tipoSolicitacao: TipoSolicitacao;
  /** Admissão: contato/endereço do Pré-cadastro na mensagem (ver ContatoColaboradorBotoes). */
  preCadastroId: string | null;
  nomeColaboradorPendente: string | null;
  /** `clientes`/`regrasCliente` (listas completas) + `solicitacaoClienteId` (fallback) em
   * vez de um nome/regra já resolvidos — porque cada Movimentação pode ser de um Cliente
   * diferente do da Solicitação (ex.: Troca de Projeto: envio pro novo, devolução do
   * antigo — ver Movimentacao.clienteId). Resolvido aqui embaixo, por Movimentação. */
  clientes: ClientSummary[];
  regrasCliente: ConfigRegrasCliente[];
  solicitacaoClienteId: string | null;
}

function novoItemIntake(id: string = crypto.randomUUID()): MovimentacaoItemIntake {
  return {
    id,
    tipoEquipamento: null,
    sistemaOperacional: null,
    homologado: null,
    propriedadeEquipamento: null,
  };
}

type PainelMovimentacao = { tipo: 'equipamento'; id: string } | { tipo: 'logistica' } | { tipo: 'termo' };

/** Id fixo só pro placeholder do 1º equipamento (Movimentação nasceu sem nenhum, ex.
 * desligamento) — vira o id real assim que o SI salva o tipo (ver MovimentacaoEquipamentoItem). */
const PLACEHOLDER_PRIMEIRO_ITEM_ID = 'primeiro-equipamento';

/**
 * Exibe e (se `podeEditar`) permite editar uma Movimentação — 100% operacional,
 * exclusivo do SI (Discovery §10.2, refletido em firestore.rules).
 *
 * Uma Movimentação pode carregar mais de um equipamento (ex. notebook + monitor no mesmo
 * envio) — cada um é um item independente (ver MovimentacaoEquipamentoItem), com seu
 * próprio tipo/SO/homologado/vínculo de patrimônio **e senha** (só pra tipos com
 * login — notebook/macbook/macmini/imac/smartphone; nunca monitor/periférico/outros).
 * Método de entrega e data continuam no nível da Movimentação (são do envio como um
 * todo, não de um equipamento específico).
 */
export default function MovimentacaoCard({
  solicitacaoId,
  movimentacao,
  podeEditar,
  podeEditarDataPrevista,
  podeExcluir,
  podeVerSenha,
  isColaboradorAlvo,
  propriedadeVisivel,
  homologadoVisivel,
  colaboradorId,
  tipoSolicitacao,
  preCadastroId,
  nomeColaboradorPendente,
  clientes,
  regrasCliente,
  solicitacaoClienteId,
}: MovimentacaoCardProps) {
  const { atualizarMovimentacao } = useMovimentacaoMutations(solicitacaoId);
  // Gaveta ao lado: um equipamento, a Logística ou o Termo — um por vez. Sem equipamento
  // nenhum ainda, já abre no 1º (placeholder) pra o SI cadastrar.
  const [painel, setPainel] = useState<PainelMovimentacao | null>(() =>
    (movimentacao.equipamentosIntake ?? []).length === 0 ? { tipo: 'equipamento', id: PLACEHOLDER_PRIMEIRO_ITEM_ID } : null
  );
  const alternarPainel = (novo: PainelMovimentacao) =>
    setPainel((atual) =>
      atual && atual.tipo === novo.tipo && (novo.tipo !== 'equipamento' || (atual.tipo === 'equipamento' && atual.id === novo.id))
        ? null
        : novo
    );

  // Cliente/projeto efetivo desta Movimentação específica — o dela (envio/devolução com
  // Cliente próprio) ou, se ainda não sabido, o da Solicitação (ver Movimentacao.clienteId).
  const clienteIdEfetivo = movimentacao.clienteId ?? solicitacaoClienteId;
  const clienteNome = clientes.find((cliente) => cliente.id === clienteIdEfetivo)?.name ?? null;
  const regraCliente = regrasCliente.find((regra) => regra.clienteId === clienteIdEfetivo);

  const handleStatusChange = (statusMovimentacao: StatusMovimentacao) => {
    atualizarMovimentacao.mutate({ id: movimentacao.id, statusMovimentacao });
  };

  const handleToggleTermo = () => {
    const atual = movimentacao.etapas.termo;
    atualizarMovimentacao.mutate({
      id: movimentacao.id,
      etapas: { termo: atual === 'concluido' ? 'pendente' : 'concluido' },
    });
  };

  const handleAdicionarEquipamento = () => {
    atualizarMovimentacao.mutate({
      id: movimentacao.id,
      equipamentosIntake: [...(movimentacao.equipamentosIntake ?? []), novoItemIntake()],
    });
  };

  const handleRemoverEquipamento = (itemId: string) => {
    atualizarMovimentacao.mutate({
      id: movimentacao.id,
      equipamentosIntake: (movimentacao.equipamentosIntake ?? []).filter((item) => item.id !== itemId),
      equipamentosExecucao: (movimentacao.equipamentosExecucao ?? []).filter((item) => item.id !== itemId),
    });
  };

  const equipamentosIntakeReais = movimentacao.equipamentosIntake ?? [];
  const itensEquipamento = mesclarEquipamentosMovimentacao(
    equipamentosIntakeReais,
    movimentacao.equipamentosExecucao
  );
  // Sem nenhum equipamento ainda (ex. desligamento, SI completa depois) — mostra o
  // formulário do 1º direto, sem precisar clicar em "+ Adicionar equipamento" primeiro.
  const itensParaExibir =
    itensEquipamento.length > 0
      ? itensEquipamento
      : mesclarEquipamentosMovimentacao([novoItemIntake(PLACEHOLDER_PRIMEIRO_ITEM_ID)], []);
  const tituloEquipamentos =
    itensEquipamento.length === 0
      ? 'nenhum equipamento ainda'
      : itensEquipamento
          .map((item) => (item.tipoEquipamento ? TIPO_EQUIPAMENTO_LABELS[item.tipoEquipamento] : 'a definir'))
          .join(' + ');

  // Tudo começa fechado; a linha que é o próximo passo fica realçada (envio: depois de
  // equipamentos e fotos; devolução: logística primeiro). Termo depois da logística.
  const etapas = etapasDaMovimentacao(movimentacao);
  const etapaOk = (id: string) => etapas.find((etapa) => etapa.id === id)?.concluida ?? false;
  const logisticaOk = etapaOk('logistica');
  const preparoOk = etapaOk('equipamentos') && etapaOk('fotos');
  const destacarLogistica = !logisticaOk && (movimentacao.tipoFluxo === 'devolucao' || preparoOk);
  const destacarTermo = logisticaOk && preparoOk && !movimentacao.termoAssinado?.url;
  const itemNoPainel = painel?.tipo === 'equipamento' ? itensParaExibir.find((item) => item.id === painel.id) ?? null : null;

  const alerta = calcularAlertaMovimentacao(movimentacao);

  return (
    <div className="rounded-md border border-gray-200 p-4">
      {/* Sempre aberta: a escolha de qual Movimentação ver é na lateral (MovimentacoesResumoLista),
          e o próximo passo de cada uma já aparece lá — sem seta nem selo de pendência aqui. */}
      <div className="flex items-center gap-3">
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <div className="text-left">
          <p className="font-montserrat font-medium text-gray-900">
            {TIPO_FLUXO_LABELS[movimentacao.tipoFluxo]} — {tituloEquipamentos}
          </p>
          {/* Visível pra quem abriu e pro SI — identifica de cara a qual projeto essa
             Movimentação se refere (pode divergir do Cliente "principal" da Solicitação,
             ex. devolução de um projeto anterior numa Troca de Projeto — ver
             Movimentacao.clienteId). */}
          <p className="text-xs text-gray-500">Projeto: {clienteNome ?? 'não informado'}</p>
          <DataPrevistaMovimentacao solicitacaoId={solicitacaoId} movimentacao={movimentacao} podeEditar={podeEditarDataPrevista} />
          <AlocacaoMovimentacaoLinha
            solicitacaoId={solicitacaoId}
            movimentacao={movimentacao}
            colaboradorId={colaboradorId}
            podeEditar={podeEditar}
          />
        </div>
        <span className="flex items-center gap-2 text-xs">
          <span
            className={`rounded-full px-2 py-0.5 font-semibold ${CLASSE_COR_POR_NIVEL_ALERTA[alerta.nivel]}`}
          >
            {alerta.emoji} {alerta.texto}
          </span>
        </span>
      </div>
      {podeExcluir && movimentacao.statusMovimentacao !== 'entregue' && (
        <ExcluirMovimentacaoButton
          solicitacaoId={solicitacaoId}
          movimentacaoId={movimentacao.id}
          descricaoMovimentacao={`${TIPO_FLUXO_LABELS[movimentacao.tipoFluxo]} — ${tituloEquipamentos}`}
        />
      )}
      </div>

      <div
        className={`mt-3 grid items-start gap-4 ${painel ? 'lg:grid-cols-[minmax(0,1fr)_440px]' : ''}`}
      >
        <div className="min-w-0 space-y-4">
          <EquipamentosMovimentacaoLista
            itens={itensParaExibir}
            tipoFluxo={movimentacao.tipoFluxo}
            abertoId={painel?.tipo === 'equipamento' ? painel.id : null}
            onAbrir={(id) => alternarPainel({ tipo: 'equipamento', id })}
            onAdicionar={podeEditar && itensEquipamento.length > 0 ? handleAdicionarEquipamento : undefined}
          />

          <FotosMovimentacaoSection
            solicitacaoId={solicitacaoId}
            movimentacao={movimentacao}
            podeEditar={podeEditar}
          />

          <div className="space-y-2">
            <LinhaSecaoMovimentacao
              titulo="Logística"
              resumo={resumoLogistica(movimentacao)}
              concluida={logisticaOk}
              destaque={destacarLogistica}
              aberta={painel?.tipo === 'logistica'}
              onAbrir={() => alternarPainel({ tipo: 'logistica' })}
            />
            {podeEditar && (
              <LinhaSecaoMovimentacao
                titulo="Termo"
                resumo={resumoTermo(movimentacao)}
                concluida={Boolean(movimentacao.termoAssinado?.url)}
                destaque={destacarTermo}
                aberta={painel?.tipo === 'termo'}
                onAbrir={() => alternarPainel({ tipo: 'termo' })}
              />
            )}
          </div>
        </div>

        {painel && (
          <GavetaMovimentacao
            titulo={painel.tipo === 'logistica' ? 'Logística' : painel.tipo === 'termo' ? 'Termo' : 'Equipamento'}
            onFechar={() => setPainel(null)}
          >
              {itemNoPainel && (
                <MovimentacaoEquipamentoItem
                  key={itemNoPainel.id}
                  solicitacaoId={solicitacaoId}
                  movimentacaoId={movimentacao.id}
                  equipamentosIntake={equipamentosIntakeReais}
                  equipamentosExecucao={movimentacao.equipamentosExecucao ?? []}
                  item={itemNoPainel}
                  podeEditar={podeEditar}
                  podeVerSenha={podeVerSenha}
                  clienteNome={clienteNome}
                  clienteId={clienteIdEfetivo}
                  colaboradorId={colaboradorId}
                  propriedadeVisivel={propriedadeVisivel}
                  homologadoVisivel={homologadoVisivel}
                  tipoFluxo={movimentacao.tipoFluxo}
                  existeNoIntake={equipamentosIntakeReais.some((equipamento) => equipamento.id === itemNoPainel.id)}
                  podeMover={itensEquipamento.length > 1}
                  onRemover={
                    equipamentosIntakeReais.some((equipamento) => equipamento.id === itemNoPainel.id)
                      ? () => {
                          handleRemoverEquipamento(itemNoPainel.id);
                          setPainel(null);
                        }
                      : undefined
                  }
                />
              )}
              {painel.tipo === 'logistica' && (
                <MovimentacaoLogisticaSection
                  solicitacaoId={solicitacaoId}
                  movimentacao={movimentacao}
                  podeEditar={podeEditar}
                  isColaboradorAlvo={isColaboradorAlvo}
                  colaboradorId={colaboradorId}
                  tipoSolicitacao={tipoSolicitacao}
                  preCadastroId={preCadastroId}
                  nomeColaboradorPendente={nomeColaboradorPendente}
                />
              )}
              {painel.tipo === 'termo' && (
                <TermoPassos
                  solicitacaoId={solicitacaoId}
                  movimentacao={movimentacao}
                  colaboradorId={colaboradorId}
                  clienteNome={clienteNome}
                  regraCliente={regraCliente}
                  onToggleEnviado={handleToggleTermo}
                />
              )}
          </GavetaMovimentacao>
        )}
      </div>

      <StatusMovimentacaoCampo movimentacao={movimentacao} podeEditar={podeEditar} onChange={handleStatusChange} />
    </div>
  );
}
